/**
 * Analysis Results Page
 * Issue #465: Display raw analysis results with tabs for different data types
 *
 * Provides views for:
 * 1. AST Summary - Per-file functions, imports, exports
 * 2. API Endpoints - Detected backend routes
 * 3. Client API Calls - Frontend fetch/axios calls
 * 4. Interactions - User interactions with/without API calls
 */

import { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import {
  api,
  type AnalysisResultsResponse,
  type ParsedFileSummary,
} from '../services/api';

// #1081 v1.0.2: 'e2e' tab removed — duplicates the canonical /flows view
// which is materially better (catalog, swimlane layout, AI enhancement,
// Copy-as-LLM-Context, etc.).
type TabType = 'ast' | 'endpoints' | 'apiCalls' | 'interactions';

// Generic type interfaces matching the API types
interface ApiEndpoint {
  id: string;
  method: string;
  path: string;
  handlerFunction?: string;
  sourceFile?: string;
  sourceLine?: number;
  evidence?: Array<{ filePath: string; lineStart: number; lineEnd: number; snippet?: string }>;
}

interface ApiCall {
  id: string;
  method: string;
  endpoint: string;
  library: string;
  sourceFile: string;
  sourceLine: number;
  sourceContext?: string;
  wrapperFunction?: string;
  evidence?: Array<{ filePath: string; lineStart: number; lineEnd: number; snippet?: string }>;
}

interface UserInteraction {
  id: string;
  name: string;
  type: string;
  triggerElement: string;
  handlerFunction: string;
  handlerFile: string;
  handlerLine: number;
  component?: string;
  associatedApiCalls: string[];
  stateChanges: string[];
  evidence?: Array<{ filePath: string; lineStart: number; lineEnd: number; snippet?: string }>;
}


// #1081: redirect legacy ?tab=e2e deep links to the canonical /flows view.
// R1+R2: render-phase short-circuit prevents the one-frame AST flash AND
// keeps the inner component's hook order stable (the early return happens
// in the wrapper, which only calls 2 hooks regardless of the query param).
export function AnalysisResultsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  if (searchParams.get('tab') === 'e2e' && projectId) {
    return <Navigate to={`/projects/${projectId}/flows`} replace />;
  }
  return <AnalysisResultsPageInner />;
}

function AnalysisResultsPageInner() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<AnalysisResultsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initialTab = (searchParams.get('tab') as TabType) || 'ast';
  const validTabs: TabType[] = ['ast', 'endpoints', 'apiCalls', 'interactions'];
  const [activeTab, setActiveTab] = useState<TabType>(validTabs.includes(initialTab) ? initialTab : 'ast');
  const [searchQuery, setSearchQuery] = useState('');

  // Update URL when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Selection state for diagram generation
  const [selectedEndpoints, setSelectedEndpoints] = useState<Set<string>>(new Set());
  const [selectedApiCalls, setSelectedApiCalls] = useState<Set<string>>(new Set());
  const [selectedInteractions, setSelectedInteractions] = useState<Set<string>>(new Set());

  // Selection state is still plumbed through to tabs for future use, but
  // there's currently no action wired to it (#1045: the
  // DiagramGenerationModal + "Generate Diagrams" button hit a backend
  // endpoint removed in #666 and were removed).

  // Fetch analysis results
  const fetchResults = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const results = await api.getAnalysisResults(projectId);
      setData(results);
    } catch (err) {
      console.error('Failed to fetch analysis results:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analysis results');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Cast raw data to typed arrays
  const apiEndpoints = useMemo(() => (data?.apiEndpoints || []) as ApiEndpoint[], [data?.apiEndpoints]);
  const apiCalls = useMemo(() => (data?.apiCalls || []) as ApiCall[], [data?.apiCalls]);
  const interactions = useMemo(() => (data?.interactions || []) as UserInteraction[], [data?.interactions]);

  // Filter functions
  const filteredParsedFiles = useMemo(() => {
    if (!data?.parsedFiles || !searchQuery.trim()) return data?.parsedFiles || [];
    const query = searchQuery.toLowerCase();
    return data.parsedFiles.filter(file =>
      file.filePath.toLowerCase().includes(query) ||
      file.functions.some(f => f.name.toLowerCase().includes(query))
    );
  }, [data?.parsedFiles, searchQuery]);

  const filteredEndpoints = useMemo(() => {
    if (!searchQuery.trim()) return apiEndpoints;
    const query = searchQuery.toLowerCase();
    return apiEndpoints.filter(ep =>
      ep.path?.toLowerCase().includes(query) ||
      ep.method?.toLowerCase().includes(query) ||
      ep.handlerFunction?.toLowerCase().includes(query)
    );
  }, [apiEndpoints, searchQuery]);

  const filteredApiCalls = useMemo(() => {
    if (!searchQuery.trim()) return apiCalls;
    const query = searchQuery.toLowerCase();
    return apiCalls.filter(call =>
      call.endpoint?.toLowerCase().includes(query) ||
      call.method?.toLowerCase().includes(query) ||
      call.sourceFile?.toLowerCase().includes(query)
    );
  }, [apiCalls, searchQuery]);

  const filteredInteractions = useMemo(() => {
    if (!searchQuery.trim()) return interactions;
    const query = searchQuery.toLowerCase();
    return interactions.filter(int =>
      int.name?.toLowerCase().includes(query) ||
      int.handlerFunction?.toLowerCase().includes(query) ||
      int.type?.toLowerCase().includes(query)
    );
  }, [interactions, searchQuery]);

  // Separate interactions by type (E2E vs Local)
  const e2eInteractions = useMemo(() =>
    filteredInteractions.filter(int => int.associatedApiCalls && int.associatedApiCalls.length > 0),
    [filteredInteractions]
  );
  const localInteractions = useMemo(() =>
    filteredInteractions.filter(int => !int.associatedApiCalls || int.associatedApiCalls.length === 0),
    [filteredInteractions]
  );

  // Selection handlers
  const toggleEndpointSelection = (id: string) => {
    setSelectedEndpoints(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleApiCallSelection = (id: string) => {
    setSelectedApiCalls(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleInteractionSelection = (id: string) => {
    setSelectedInteractions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllEndpoints = () => setSelectedEndpoints(new Set(filteredEndpoints.map(e => e.id)));
  const clearEndpointSelection = () => setSelectedEndpoints(new Set());

  const selectAllApiCalls = () => setSelectedApiCalls(new Set(filteredApiCalls.map(c => c.id)));
  const clearApiCallSelection = () => setSelectedApiCalls(new Set());

  const selectAllE2EInteractions = () => setSelectedInteractions(new Set(e2eInteractions.map(i => i.id)));
  const clearInteractionSelection = () => setSelectedInteractions(new Set());

  const totalSelected = selectedEndpoints.size + selectedApiCalls.size + selectedInteractions.size;

  // #1074 C1: jump to the dedicated Call Graph page focused on this file.
  // CallGraphPage reads `?rootFile=<encoded path>` and pre-sets the subgraph
  // root to the first function found in that file.
  const handleGenerateCallGraph = useCallback((filePath: string) => {
    if (!projectId) return;
    navigate(`/projects/${projectId}/call-graph?rootFile=${encodeURIComponent(filePath)}`);
  }, [projectId, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          <p className="text-slate-600 dark:text-slate-400">Loading analysis results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
            Error Loading Code Details
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={fetchResults}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
            <Link
              to={`/projects/${projectId}`}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Back to Project
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-slate-600 dark:text-slate-400">No analysis results available. Run analysis first.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to={`/projects/${projectId}`}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              &larr; Back to Project
            </Link>
            <h1 className="text-xl font-semibold text-slate-800 dark:text-white">
              Code Details
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {totalSelected > 0 && (
              <span className="text-sm text-blue-600 dark:text-blue-400">
                {totalSelected} items selected
              </span>
            )}
            {/* #1045: "Generate Diagrams" button removed — it opened a
                modal that POST'd to /api/diagrams/generate-from-analysis,
                an endpoint dropped in #666 along with the legacy
                GeneratedDiagram model. */}
          </div>
        </div>
      </header>

      {/* Stats bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-2 flex items-center gap-6 text-sm">
        <span className="text-slate-600 dark:text-slate-400">
          <strong className="text-slate-800 dark:text-white">{data.astSummary.totalFiles}</strong> files
        </span>
        <span className="text-slate-600 dark:text-slate-400">
          <strong className="text-slate-800 dark:text-white">{data.astSummary.totalFunctions}</strong> functions
        </span>
        <span className="text-slate-600 dark:text-slate-400">
          <strong className="text-slate-800 dark:text-white">{apiEndpoints.length}</strong> endpoints
        </span>
        <span className="text-slate-600 dark:text-slate-400">
          <strong className="text-slate-800 dark:text-white">{apiCalls.length}</strong> API calls
        </span>
        <span className="text-slate-600 dark:text-slate-400">
          <strong className="text-slate-800 dark:text-white">{interactions.length}</strong> interactions
        </span>
        <span className="ml-auto text-slate-500 dark:text-slate-400">
          Analyzed: {new Date(data.createdAt).toLocaleString()}
        </span>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {[
              { id: 'ast' as TabType, label: 'AST Summary', count: data.parsedFiles.length },
              { id: 'endpoints' as TabType, label: 'API Endpoints', count: apiEndpoints.length },
              { id: 'apiCalls' as TabType, label: 'Client API Calls', count: apiCalls.length },
              { id: 'interactions' as TabType, label: 'Interactions', count: interactions.length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-3">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'ast' && (
          <ASTSummaryTab
            files={filteredParsedFiles}
            summary={data.astSummary}
            onGenerateCallGraph={handleGenerateCallGraph}
          />
        )}

        {activeTab === 'endpoints' && (
          <ApiEndpointsTab
            endpoints={filteredEndpoints}
            selectedIds={selectedEndpoints}
            onToggleSelection={toggleEndpointSelection}
            onSelectAll={selectAllEndpoints}
            onClearSelection={clearEndpointSelection}
          />
        )}

        {activeTab === 'apiCalls' && (
          <ApiCallsTab
            apiCalls={filteredApiCalls}
            selectedIds={selectedApiCalls}
            onToggleSelection={toggleApiCallSelection}
            onSelectAll={selectAllApiCalls}
            onClearSelection={clearApiCallSelection}
          />
        )}

        {activeTab === 'interactions' && (
          <InteractionsTab
            e2eInteractions={e2eInteractions}
            localInteractions={localInteractions}
            selectedIds={selectedInteractions}
            onToggleSelection={toggleInteractionSelection}
            onSelectAll={selectAllE2EInteractions}
            onClearSelection={clearInteractionSelection}
          />
        )}

        {/* #1081 v1.0.2: 'e2e' tab + E2EFlowsTab component removed; the
            canonical view is /projects/:id/flows. ?tab=e2e deep links
            redirect via the useEffect at the top of this component. */}
      </div>

      {/* #1045: DiagramGenerationModal removed — its POST endpoint was
          dropped in #666. See note at the selection state above. */}
    </div>
  );
}

// ============ Tab Components ============

interface ASTSummaryTabProps {
  files: ParsedFileSummary[];
  summary: AnalysisResultsResponse['astSummary'];
  onGenerateCallGraph?: (filePath: string) => void;
}

function ASTSummaryTab({ files, summary, onGenerateCallGraph }: ASTSummaryTabProps) {
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());

  const toggleExpanded = (filePath: string) => {
    setExpandedFiles(prev => {
      const next = new Set(prev);
      if (next.has(filePath)) next.delete(filePath);
      else next.add(filePath);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-slate-800 dark:text-white">{summary.totalFiles}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Total Files</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">{summary.parsedCount}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Parsed</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-red-600">{summary.errorCount}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Errors</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">{summary.totalFunctions}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Functions</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-purple-600">{summary.totalImports}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Imports</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-amber-600">{summary.totalExports}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Exports</div>
        </div>
      </div>

      {/* Files Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">File</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Imports</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Exports</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Functions</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {files.map(file => {
              const isExpanded = expandedFiles.has(file.filePath);
              const hasError = file.errors && file.errors.length > 0;
              return (
                // #1074 C4: Fragment needs the key so React doesn't warn about
                // duplicate keys when several rows from the same file render
                // (the previous `<>` wrapper had no key; the inner <tr key>
                // wasn't enough because Fragment is the outer iteration node).
                <Fragment key={file.filePath}>
                  <tr
                    onClick={() => toggleExpanded(file.filePath)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                          &gt;
                        </span>
                        <span className="font-mono text-sm text-gray-800 dark:text-gray-200">
                          {file.filePath.split('/').pop()}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 ml-5 truncate max-w-md">
                        {file.filePath}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">
                      {file.imports.length}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">
                      {file.exports.length}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">
                      {file.functions.length}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded ${hasError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {hasError ? 'Error' : 'OK'}
                      </span>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${file.filePath}-details`}>
                      <td colSpan={5} className="px-4 py-3 bg-gray-50 dark:bg-gray-900">
                        <div className="grid grid-cols-3 gap-4 pl-6">
                          <div>
                            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Imports</h4>
                            <ul className="text-sm space-y-1">
                              {file.imports.slice(0, 10).map((imp, idx) => (
                                <li key={idx} className="text-gray-600 dark:text-gray-300 font-mono text-xs">
                                  {imp.source}
                                </li>
                              ))}
                              {file.imports.length > 10 && (
                                <li className="text-gray-400">+{file.imports.length - 10} more</li>
                              )}
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Exports</h4>
                            <ul className="text-sm space-y-1">
                              {file.exports.slice(0, 10).map((exp, idx) => (
                                <li key={idx} className="text-gray-600 dark:text-gray-300 font-mono text-xs">
                                  {exp}
                                </li>
                              ))}
                              {file.exports.length > 10 && (
                                <li className="text-gray-400">+{file.exports.length - 10} more</li>
                              )}
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Functions</h4>
                            <ul className="text-sm space-y-1">
                              {file.functions.slice(0, 10).map((fn, idx) => (
                                <li key={idx} className="text-gray-600 dark:text-gray-300 font-mono text-xs">
                                  {fn.isAsync && <span className="text-purple-500">async </span>}
                                  {fn.name}
                                  <span className="text-gray-400 ml-1">:{fn.line}</span>
                                </li>
                              ))}
                              {file.functions.length > 10 && (
                                <li className="text-gray-400">+{file.functions.length - 10} more</li>
                              )}
                            </ul>
                          </div>
                        </div>
                        {/* Function Calls (Call Graph) */}
                        {file.functionCalls && file.functionCalls.length > 0 && (
                          <div className="mt-4 pl-6">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Function Calls ({file.functionCalls.length})
                              </h4>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onGenerateCallGraph?.(file.filePath);
                                }}
                                className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                              >
                                View in Call Graph
                              </button>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-100 dark:bg-gray-700">
                                  <tr>
                                    <th className="px-2 py-1 text-left text-gray-500 dark:text-gray-400">Caller</th>
                                    <th className="px-2 py-1 text-center text-gray-500 dark:text-gray-400"></th>
                                    <th className="px-2 py-1 text-left text-gray-500 dark:text-gray-400">Callee</th>
                                    <th className="px-2 py-1 text-right text-gray-500 dark:text-gray-400">Line</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                  {file.functionCalls.slice(0, 15).map((call, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                      <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300">
                                        {call.isAsync && <span className="text-purple-500">async </span>}
                                        {call.caller}
                                      </td>
                                      <td className="px-2 py-1 text-center text-gray-400">&rarr;</td>
                                      <td className="px-2 py-1 font-mono text-blue-600 dark:text-blue-400">
                                        {call.callee}
                                      </td>
                                      <td className="px-2 py-1 text-right text-gray-400">
                                        :{call.line}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {file.functionCalls.length > 15 && (
                                <div className="px-2 py-1 text-xs text-gray-400 bg-gray-50 dark:bg-gray-800">
                                  +{file.functionCalls.length - 15} more calls
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {file.errors && file.errors.length > 0 && (
                          <div className="mt-3 pl-6">
                            <h4 className="text-xs font-medium text-red-500 uppercase mb-2">Errors</h4>
                            <ul className="text-sm space-y-1">
                              {file.errors.map((err, idx) => (
                                <li key={idx} className="text-red-600 text-xs">{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface ApiEndpointsTabProps {
  endpoints: ApiEndpoint[];
  selectedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

function ApiEndpointsTab({ endpoints, selectedIds, onToggleSelection, onSelectAll, onClearSelection }: ApiEndpointsTabProps) {
  const methodColors: Record<string, string> = {
    GET: 'bg-green-100 text-green-800',
    POST: 'bg-blue-100 text-blue-800',
    PUT: 'bg-amber-100 text-amber-800',
    PATCH: 'bg-yellow-100 text-yellow-800',
    DELETE: 'bg-red-100 text-red-800',
    WS: 'bg-purple-100 text-purple-800',
    SSE: 'bg-orange-100 text-orange-800',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {selectedIds.size} of {endpoints.length} selected
        </span>
        <div className="flex gap-2">
          <button onClick={onSelectAll} className="text-sm text-blue-600 hover:underline">Select All</button>
          <button onClick={onClearSelection} className="text-sm text-gray-500 hover:underline">Clear</button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 w-10"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Method</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Path</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Handler</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">File</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {endpoints.map(ep => (
              <tr
                key={ep.id}
                onClick={() => onToggleSelection(ep.id)}
                className={`cursor-pointer transition-colors ${
                  selectedIds.has(ep.id)
                    ? 'bg-blue-50 dark:bg-blue-900/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(ep.id)}
                    onChange={() => onToggleSelection(ep.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${methodColors[ep.method] || 'bg-gray-100 text-gray-800'}`}>
                    {ep.method}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-sm text-gray-800 dark:text-gray-200">
                  {ep.path}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {ep.handlerFunction || '-'}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                  {ep.sourceFile?.split('/').pop() || '-'}
                  {ep.sourceLine && <span className="ml-1">:{ep.sourceLine}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface ApiCallsTabProps {
  apiCalls: ApiCall[];
  selectedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

function ApiCallsTab({ apiCalls, selectedIds, onToggleSelection, onSelectAll, onClearSelection }: ApiCallsTabProps) {
  const methodColors: Record<string, string> = {
    GET: 'bg-green-100 text-green-800',
    POST: 'bg-blue-100 text-blue-800',
    PUT: 'bg-amber-100 text-amber-800',
    PATCH: 'bg-yellow-100 text-yellow-800',
    DELETE: 'bg-red-100 text-red-800',
    WS: 'bg-purple-100 text-purple-800',
    SSE: 'bg-orange-100 text-orange-800',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {selectedIds.size} of {apiCalls.length} selected
        </span>
        <div className="flex gap-2">
          <button onClick={onSelectAll} className="text-sm text-blue-600 hover:underline">Select All</button>
          <button onClick={onClearSelection} className="text-sm text-gray-500 hover:underline">Clear</button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 w-10"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Method</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Endpoint</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Library</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Caller</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">File</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {apiCalls.map(call => (
              <tr
                key={call.id}
                onClick={() => onToggleSelection(call.id)}
                className={`cursor-pointer transition-colors ${
                  selectedIds.has(call.id)
                    ? 'bg-blue-50 dark:bg-blue-900/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(call.id)}
                    onChange={() => onToggleSelection(call.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${methodColors[call.method] || 'bg-gray-100 text-gray-800'}`}>
                    {call.method}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-sm text-gray-800 dark:text-gray-200">
                  {call.endpoint}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {call.library}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {call.wrapperFunction || call.sourceContext || '-'}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                  {call.sourceFile?.split('/').pop()}:{call.sourceLine}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface InteractionsTabProps {
  e2eInteractions: UserInteraction[];
  localInteractions: UserInteraction[];
  selectedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

function InteractionsTab({
  e2eInteractions,
  localInteractions,
  selectedIds,
  onToggleSelection,
  onSelectAll,
  onClearSelection,
}: InteractionsTabProps) {
  const typeColors: Record<string, string> = {
    click: 'bg-blue-100 text-blue-800',
    submit: 'bg-green-100 text-green-800',
    navigate: 'bg-purple-100 text-purple-800',
    fetch: 'bg-amber-100 text-amber-800',
    gesture: 'bg-pink-100 text-pink-800',
    realtime: 'bg-cyan-100 text-cyan-800',
    file: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex gap-4">
        <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 flex-1">
          <div className="text-lg font-semibold text-green-800 dark:text-green-200">
            {e2eInteractions.length} E2E Interactions
          </div>
          <div className="text-sm text-green-600 dark:text-green-300">
            Triggers with API calls - full client-server flow
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex-1">
          <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            {localInteractions.length} Local Interactions
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Navigation, DOM changes, state updates
          </div>
        </div>
      </div>

      {/* E2E Interactions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            E2E Interactions (triggers API calls)
          </h3>
          <div className="flex gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {selectedIds.size} selected
            </span>
            <button onClick={onSelectAll} className="text-sm text-blue-600 hover:underline">Select All</button>
            <button onClick={onClearSelection} className="text-sm text-gray-500 hover:underline">Clear</button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 w-10"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Trigger</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Handler</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">API Calls</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">File</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {e2eInteractions.map(int => (
                <tr
                  key={int.id}
                  onClick={() => onToggleSelection(int.id)}
                  className={`cursor-pointer transition-colors ${
                    selectedIds.has(int.id)
                      ? 'bg-blue-50 dark:bg-blue-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(int.id)}
                      onChange={() => onToggleSelection(int.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${typeColors[int.type] || 'bg-gray-100 text-gray-800'}`}>
                      {int.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 dark:text-gray-200">{int.name}</div>
                    <div className="text-xs text-gray-500">{int.triggerElement}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600 dark:text-gray-300">
                    {int.handlerFunction}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {int.associatedApiCalls.length} call(s)
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                    {int.handlerFile?.split('/').pop()}:{int.handlerLine}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Local Interactions */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">
          Local Interactions (no API)
        </h3>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Element</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Handler</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Effect</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">File</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {localInteractions.slice(0, 50).map(int => (
                <tr key={int.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${typeColors[int.type] || 'bg-gray-100 text-gray-800'}`}>
                      {int.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 dark:text-gray-200">{int.name}</div>
                    <div className="text-xs text-gray-500">{int.triggerElement}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600 dark:text-gray-300">
                    {int.handlerFunction}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {int.stateChanges.length > 0 ? `${int.stateChanges.length} state change(s)` : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                    {int.handlerFile?.split('/').pop()}:{int.handlerLine}
                  </td>
                </tr>
              ))}
              {localInteractions.length > 50 && (
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-center text-sm text-gray-500">
                    + {localInteractions.length - 50} more interactions
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


export default AnalysisResultsPage;
